<template>
  <div class="resize-container-9 min-h-screen flex relative lg:static">
    <div
        id="app-sidebar-emoji"
        class="w-[280px] h-screen hidden lg:flex shrink-0 absolute lg:static left-0 top-0 z-10 select-none bg-surface-0 dark:bg-surface-950 border-r border-surface-200 dark:border-surface-700"
    >
      <div class="flex flex-col h-full w-full">
        <div class="flex items-center p-4 gap-4 shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" width="43" height="43" viewBox="0 0 43 43" fill="none"
               class="w-10 h-10">
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M21.5 42.0498C33.098 42.0498 42.5 32.6477 42.5 21.0498C42.5 9.45183 33.098 0.0498047 21.5 0.0498047C9.902 0.0498047 0.5 9.45183 0.5 21.0498C0.5 32.6477 9.902 42.0498 21.5 42.0498ZM28.0513 9.83248C28.3702 8.69975 27.2709 8.02994 26.267 8.74516L12.2528 18.7288C11.164 19.5045 11.3353 21.0498 12.51 21.0498H16.2003V21.0212H23.3926L17.5323 23.089L14.9487 32.2671C14.6299 33.3999 15.729 34.0697 16.733 33.3544L30.7472 23.3708C31.836 22.5951 31.6646 21.0498 30.49 21.0498H24.8937L28.0513 9.83248Z"
                class="fill-surface-900 dark:fill-surface-0"
            />
          </svg>
          <span class="text-lg font-semibold leading-tight text-surface-900 dark:text-surface-0">CADSYS</span>
        </div>

        <!-- NAVIGATION MENU -->
        <div
            class="flex-1 overflow-y-auto scroll-auto-hide
   [--scrollbar-thumb:theme(colors.surface-200)]
   dark:[--scrollbar-thumb:theme(colors.surface-800)]">

          <!-- OVERVIEW SECTION -->
          <section>
            <div class="bg-surface-0 dark:bg-surface-950">
              <div class="bg-surface-0 dark:bg-surface-950 select-none p-4 max-w-84 mx-auto">
                <div class="p-4 flex items-center text-surface-900 dark:text-surface-0">
                  <span class="font-medium text-lg leading-tight">Overview</span>
                </div>

                <ul class="list-none pl-4 m-0 gap-0">

                  <!-- Dashboard -->
                  <li class="flex overflow-hidden" @click="navigate('dashboard')">
                    <div
                        class="border-l-2 flex flex-col items-center justify-center z-10"
                        :class="{ 'border-primary': activeTab == 'overview-dashboard', 'border-surface': activeTab != 'overview-dashboard'}"
                    />
                    <a
                        class="flex flex-1 cursor-pointer p-2 hover:bg-emphasis transition-colors duration-150 text-base leading-tight"
                        :class="{ 'text-surface-900 dark:text-surface-0': activeTab === 'overview-dashboard', 'text-surface-500 dark:text-surface-400': activeTab !== 'overview-dashboard' }"
                        @click="activeTab = 'overview-dashboard'"
                    >
                      <LucideIcon name="LayoutDashboard"/>
                      <span class="flex-1 ml-2">Dashboard</span>
                    </a>
                  </li>

                  <!-- AI Assistant -->
                  <li class="flex overflow-hidden">
                    <div
                        class="border-l-2 flex flex-col items-center justify-center z-10"
                        :class="{ 'border-primary': activeTab == 'overview-ai', 'border-surface': activeTab != 'overview-ai'}"
                    />
                    <a
                        class="flex flex-1 cursor-pointer p-2 hover:bg-emphasis transition-colors duration-150 text-base leading-tight"
                        :class="{ 'text-surface-900 dark:text-surface-0': activeTab === 'overview-ai', 'text-surface-500 dark:text-surface-400': activeTab !== 'overview-ai' }"
                        @click="activeTab = 'overview-ai'"
                    >
                      <LucideIcon name="Sparkles"/>
                      <span class="flex-1 ml-2">AI Assistant</span>
                    </a>
                  </li>

                  <!-- Live Help -->
                  <li class="flex overflow-hidden">
                    <div
                        class="border-l-2 flex flex-col items-center justify-center z-10"
                        :class="{ 'border-primary': activeTab == 'overview-help', 'border-surface': activeTab != 'overview-help'}"
                    />
                    <a
                        class="flex flex-1 cursor-pointer p-2 hover:bg-emphasis transition-colors duration-150 text-base leading-tight"
                        :class="{ 'text-surface-900 dark:text-surface-0': activeTab === 'overview-help', 'text-surface-500 dark:text-surface-400': activeTab !== 'overview-help' }"
                        @click="activeTab = 'overview-help'"
                    >
                      <LucideIcon name="MessageCircleQuestionMark"/>
                      <span class="flex-1 ml-2">Live Help</span>
                      <Badge severity="success" class="mt-2"/>
                    </a>
                  </li>


                </ul>
              </div>
            </div>
          </section>

          <!-- APPLICATION SECTION -->
          <section>
            <div class="bg-surface-0 dark:bg-surface-950">
              <div class="bg-surface-0 dark:bg-surface-950 select-none p-4 max-w-84 mx-auto">
                <div class="p-4 flex items-center text-surface-900 dark:text-surface-0">
                  <span class="font-medium text-lg leading-tight">Application</span>
                </div>

                <ul class="list-none pl-4 m-0 gap-0">

                  <!-- ENGINEERING -->
                  <li class="flex flex-col overflow-hidden">
                    <div class="flex overflow-hidden">
                      <div
                          class="border-l-2 flex flex-col items-center justify-center z-10 border-surface"
                      />
                      <a
                          v-styleclass="{
        selector: '.engineering-submenu',
        enterFromClass: 'hidden',
        enterActiveClass: 'animate-slidedown',
        leaveToClass: 'hidden',
        leaveActiveClass: 'animate-slideup'
      }"
                          class="flex items-center justify-between flex-1 cursor-pointer p-2 hover:bg-emphasis transition-colors duration-150 text-base leading-tight text-surface-500 dark:text-surface-400"
                      >

                        <LucideIcon name="HardHat"/>
                        <span class="flex-1 ml-2">Engineering</span>

                        <i
                            class="pi pi-angle-down text-base leading-none text-surface-500 dark:text-surface-400"
                        />
                      </a>
                    </div>

                    <!-- UNIT INFORMATION MENU -->
                    <ul class="engineering-submenu w-full m-0 p-0 overflow-hidden hidden">
                      <li class="flex flex-col overflow-hidden">
                        <div class="flex overflow-hidden">
                          <div
                              class="border-l-2 flex flex-col items-center justify-center z-10"
                              :class="{
            'border-primary': activeTab == 'property-unit-information',
            'border-surface': activeTab != 'property-unit-information'
          }"
                          />
                          <a
                              v-styleclass="{
            selector: '.unit-info-submenu',
            enterFromClass: 'hidden',
            enterActiveClass: 'animate-slidedown',
            leaveToClass: 'hidden',
            leaveActiveClass: 'animate-slideup'
          }"
                              class="flex items-center justify-between flex-1 cursor-pointer py-2 pl-8 pr-2 hover:bg-emphasis transition-colors duration-150 text-base leading-tight text-surface-500 dark:text-surface-400"
                          >
                            <span class="font-medium">Unit Information</span>
                            <i
                                class="pi pi-angle-down text-base leading-none text-surface-500 dark:text-surface-400"
                            />
                          </a>
                        </div>

                        <!-- SUBMENU ITEMS -->
                        <ul class="unit-info-submenu w-full m-0 p-0 overflow-hidden hidden">
                          <li class="flex overflow-hidden">
                            <div
                                class="border-l-2 flex flex-col items-center justify-center z-10"
                                :class="{
              'border-primary': activeTab == 'property-unit-information-unit-list',
              'border-surface': activeTab != 'property-unit-information-unit-list'
            }"
                            />
                            <a
                                class="flex-1 cursor-pointer py-2 pl-12 pr-2 hover:bg-emphasis transition-colors duration-150 text-base leading-tight text-surface-500 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-0"
                                @click="activeTab = 'property-unit-information-unit-list'"
                            >
                              Unit List
                            </a>
                          </li>
                          <li class="flex overflow-hidden">
                            <div
                                class="border-l-2 flex flex-col items-center justify-center z-10"
                                :class="{
              'border-primary': activeTab == 'property-unit-information-expiring-contracts',
              'border-surface': activeTab != 'property-unit-information-expiring-contracts'
            }"
                            />
                            <a
                                class="flex-1 cursor-pointer py-2 pl-12 pr-2 hover:bg-emphasis transition-colors duration-150 text-base leading-tight text-surface-500 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-0"
                                @click="activeTab = 'property-unit-information-expiring-contracts'"
                            >
                              Expiring Contracts
                            </a>
                          </li>
                        </ul>
                      </li>

                      <!-- DOMESTIC STAFF -->
                      <li class="flex overflow-hidden">
                        <div
                            class="border-l-2 flex flex-col items-center justify-center z-10"
                            :class="{ 'border-primary': activeTab == 'property-domestic-staff', 'border-surface': activeTab != 'property-domestic-staff' }"
                        />
                        <a
                            class="flex-1 cursor-pointer py-2 pl-8 pr-2 hover:bg-emphasis transition-colors duration-150 text-base leading-tight"
                            :class="{
          'text-surface-900 dark:text-surface-0': activeTab === 'property-domestic-staff',
          'text-surface-500 dark:text-surface-400': activeTab !== 'property-domestic-staff'
        }"
                            @click="activeTab = 'property-domestic-staff'"
                        >
                          <span class="font-medium">Domestic Staff</span>
                        </a>
                      </li>
                    </ul>
                  </li>
                  


                </ul>
              </div>
            </div>
          </section>
        </div>
        <!-- END NAVIGATION MENU -->


        <div class="py-2 mt-auto border-surface-200 dark:border-surface-800 border-t">
          <ul class="list-none p-2 m-0 hidden animate-duration-150 overflow-hidden">
            <li>
              <a
                  class="flex items-center cursor-pointer p-3 gap-2 rounded-lg text-surface-700 dark:text-surface-200 hover:bg-surface-50 dark:hover:bg-surface-800 border border-transparent hover:border-surface-200 dark:hover:border-surface-700 transition-colors duration-150 hover:text-surface-900 dark:hover:text-surface-50"
              >
                <LucideIcon name="User"/>
                <span class="font-medium text-base leading-tight">Profile</span>
              </a>
            </li>
            <li @click="ToggleDarkMode()">
              <a
                  class="flex items-center cursor-pointer p-3 gap-2 rounded-lg text-surface-700 dark:text-surface-200 hover:bg-surface-50 dark:hover:bg-surface-800 border border-transparent hover:border-surface-200 dark:hover:border-surface-700 transition-colors duration-150 hover:text-surface-900 dark:hover:text-surface-50"
              >
                <LucideIcon :name="iconForDarkModeToggle"/>
                <span class="font-medium text-base leading-tight">Switch mode</span>
              </a>
            </li>
            <li>
              <a
                  class="flex items-center cursor-pointer p-3 gap-2 rounded-lg text-surface-700 dark:text-surface-200 hover:bg-surface-50 dark:hover:bg-surface-800 border border-transparent hover:border-surface-200 dark:hover:border-surface-700 transition-colors duration-150 hover:text-surface-900 dark:hover:text-surface-50"
              >
                <LucideIcon name="Settings"/>
                <span class="font-medium text-base leading-tight">Settings</span>
              </a>
            </li>
            <li @click="logout()">
              <a
                  class="flex items-center cursor-pointer p-3 gap-2 rounded-lg text-surface-700 dark:text-surface-200 hover:bg-surface-50 dark:hover:bg-surface-800 border border-transparent hover:border-surface-200 dark:hover:border-surface-700 transition-colors duration-150 hover:text-surface-900 dark:hover:text-surface-50"
              >
                <LucideIcon name="LogOut"/>
                <span class="font-medium text-base leading-tight">Sign Out</span>
              </a>
            </li>
          </ul>
          <a
              v-styleclass="{
                            selector: '@prev',
                            enterFromClass: 'hidden',
                            enterActiveClass: 'animate-slidedown',
                            leaveToClass: 'hidden',
                            leaveActiveClass: 'animate-slideup'
                        }"
              class="flex items-center cursor-pointer p-2 gap-2 text-surface-900 dark:text-surface-0 hover: transition-colors duration-150"
          >
            <img
                src="https://fqjltiegiezfetthbags.supabase.co/storage/v1/render/image/public/block.images/blocks/avatars/avatar-amyels.png"
                class="w-8 h-8 rounded-full cursor-pointer"/>
            <span class="font-medium text-base leading-tight">Amy Elsner</span>
            <i class="pi pi-angle-up text-base! leading-none! text-surface-500 dark:text-surface-400 ml-auto"/>
          </a>
        </div>
      </div>
    </div>

    <!-- RIGHT SIDE -->
    <div class="flex flex-col h-screen flex-auto">

      <!-- TOP NAV (fixed height, never scrolls) -->
      <div
          class="shrink-0 flex justify-between items-center py-4 px-8 bg-surface-0 dark:bg-surface-950
             border-b border-surface-200 dark:border-surface-700">
        <div class="flex items-center gap-8">
          <a
              v-styleclass="{
            selector: '#app-sidebar-emoji',
            enterFromClass: 'hidden',
            enterActiveClass: 'animate-fadeinleft',
            leaveToClass: 'hidden',
            leaveActiveClass: 'animate-fadeoutleft',
            hideOnOutsideClick: true,
            resizeSelector: '.resize-container-9',
            hideOnResize: true
          }"
              class="cursor-pointer flex items-center justify-center lg:hidden text-surface-700 dark:text-surface-100"
          >
            <i class="pi pi-bars text-xl leading-none"/>
          </a>

          <i class="pi pi-inbox text-xl leading-none text-surface-700 dark:text-surface-400 hover:text-primary-emphasis"/>
          <i class="pi pi-sparkles text-xl leading-none text-surface-700 dark:text-surface-400 hover:text-primary-emphasis"/>
          <i class="pi pi-address-book text-xl leading-none text-surface-700 dark:text-surface-400 hover:text-primary-emphasis"/>
        </div>

        <div class="flex items-center font-bold">
          {{ auth.user?.communityName ? auth.user.communityName.toUpperCase() : '' }}
        </div>
      </div>

      <!-- MAIN CONTENT (scrollable area) -->
      <div class="flex-1 overflow-y-auto scroll-auto-hide [--scrollbar-thumb:theme(colors.surface-300)]
   dark:[--scrollbar-thumb:theme(colors.surface-800)]">
        <div class="p-8 max-w-7xl mx-auto">
          <router-view/>
        </div>
      </div>


    </div>
  </div>
</template>


<script setup>
import {useAuthStore} from '@/stores/auth';
import LucideIcon from "@/components/LucideIconComponent.vue";
import {computed, onMounted, ref} from "vue";
import router from "@/router/index.js";

const auth = useAuthStore();
const darkMode = ref(null);
const iconForDarkModeToggle = computed(() => (darkMode.value ? 'Moon' : 'Sun'));
const activeTab = ref('organization-overview');

const navigate = (destination) => {
  router.push(destination);
}
const ToggleDarkMode = () => {
  if (localStorage.getItem('dark_mode') === 'true') {
    localStorage.setItem('dark_mode', 'false');
    document.documentElement.classList.remove('my-app-dark');
  } else {
    localStorage.setItem('dark_mode', 'true');
    document.documentElement.classList.add('my-app-dark');
  }

  SetDarkModeIcon();
}

const SetDarkModeIcon = () => {
  darkMode.value = document.documentElement.classList.contains('my-app-dark');
}

const InitDarkMode = () => {
  const storedPref = localStorage.getItem('dark_mode');
  console.log(storedPref);

  // Default to dark mode if nothing is stored yet
  const shouldBeDark = storedPref === null ? true : storedPref === 'true';
  console.log(shouldBeDark);

  if (shouldBeDark) {
    document.documentElement.classList.add('my-app-dark');
  } else {
    document.documentElement.classList.remove('my-app-dark');
  }

  localStorage.setItem('dark_mode', shouldBeDark.toString());
  SetDarkModeIcon();
};

const logout = async () => {
  try {
    await auth.logoutUser();

    // Navigate to login page
    await router.push({name: 'login-adm'});
  } catch (error) {
    console.error('Logout failed:', error);
  }
};

onMounted(() => {
  InitDarkMode();
})
</script>

<style scoped>
.scroll-auto-hide::-webkit-scrollbar {
  width: 8px;
}

.scroll-auto-hide::-webkit-scrollbar-track {
  background: transparent;
}

.scroll-auto-hide::-webkit-scrollbar-thumb {
  background-color: var(--scrollbar-thumb);
  border-radius: 9999px;
  opacity: 0;
  transition: opacity 0.5s;
}

.scroll-auto-hide:hover::-webkit-scrollbar-thumb,
.scroll-auto-hide:active::-webkit-scrollbar-thumb {
  opacity: 1;
}

/* Firefox */
.scroll-auto-hide {
  scrollbar-width: thin;
  scrollbar-color: var(--scrollbar-thumb) transparent;
}

</style>